import React, { useEffect, useState } from "react";
import Layout from "../../../components/Layout/Layout";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import InputField from "../../../components/UI/Forms/InputField";
import ErrorMessage from "../../../components/UI/Notifications/ErrorMessage";
import Button from "../../../components/UI/Buttons/Button";
import { pageLinks, showToastNotification } from "../../../utility/helper";
import { callApi } from "../../../api/callApi";
import { API_ENDPOINT, API_URL_2 } from "../../../api/constant";
import SelectDropdown from "../../../components/UI/Forms/SelectDropdown";
import CKEditorField from "../../../components/UI/Forms/CKEditorField";
import useImageUpload from "../../../hooks/useImageUpload";
import useFileDelete from "../../../hooks/useFileDelete";


const AddSection: React.FC = () => {
    const { uploadImage } = useImageUpload();
    const { deleteFile } = useFileDelete();
    const { register, handleSubmit, setValue, control, formState: { errors } } = useForm({ mode: 'onBlur' });
    const navigate = useNavigate();
    const { id } = useParams(); // Get the section ID from the URL
    const [loading, setLoading] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false); // Track if it's edit mode
    const [sections, setSections] = useState<{
        image: boolean,
        icon: boolean,
        subSection: boolean,
        button: boolean,
        link: boolean
    }>({ image: false, icon: false, subSection: false, button: false, link: false });
    const [imageURL, setImageURl] = useState<{ icon: string, images: string[] }>({ icon: '', images: [] });

    useEffect(() => {
        if (id) {
            fetchSectionData(id);
            setIsEdit(true);
        }
    }, [id]);

    const fetchSectionData = async (sectionId: string) => {
        try {
            const response = await callApi(API_URL_2 + API_ENDPOINT.page + `sections/detail/${sectionId}`, { method: 'GET' });
            const section = response;

            // Populate form fields
            setValue('page', section.page);
            setValue('title', section.title);
            setValue('content', section.content);
            setValue('order', section.order);
            setValue('sub_content', section.subSection?.content);
            setValue('sub_heading', section.subSection?.heading);
            setValue('button_title', section.button?.title);
            setValue('button_url', section.button?.url);
            setValue('link_title', section.link?.text);
            setValue('link_url', section.link?.url);

            // Set image URLs
            setImageURl({
                icon: section.icon || '',
                images: section.imageUrls || []
            });

            // Toggle sections based on available data
            setSections({
                image: !!section.imageUrls?.length,
                icon: !!section.icon,
                subSection: Object.keys(section.subSection).length > 0,
                button: Object.keys(section.button).length > 0,
                link: Object.keys(section.link).length > 0,
            });
        } catch (error: any) {
            console.log('danger', error.message);
        }
    };

    const onSubmit = async (data: any) => {
        setLoading(true);

        const postData = {
            page: data.page,
            sectionType: "hero",
            title: data.title,
            content: data.content,
            order: data.order,
            icon: imageURL.icon,
            imageUrls: imageURL.images,
            subSection: {
                content: data.sub_content,
                heading: data.sub_heading,
            },
            button: {
                title: data.button_title,
                url: data.button_url,
            },
            link: {
                url: data.link_url,
                text: data.link_title,
            },
        };

        try {
            if (isEdit) {
                // Update section
                await callApi(API_URL_2 + API_ENDPOINT.page + `sections/${id}`, { method: 'PUT', data: postData });
                showToastNotification('success', 'Section updated successfully!');
            } else {
                // Create new section
                await callApi(API_URL_2 + API_ENDPOINT.page + 'sections', { method: 'POST', data: postData });
                showToastNotification('success', 'Section created successfully!');
            }

            setLoading(false);
            navigate('/sections');
        } catch (error: any) {
            setLoading(false);
            showToastNotification('danger', error.message);
        }
    };

    const toggleSection = (section: keyof typeof sections) => {
        setSections((prevSections) => ({
            ...prevSections,
            [section]: !prevSections[section],
        }));
    };

    const handleFileChange = async (event: any, isMultiple: boolean) => {
        const files = isMultiple ? Array.from(event.target.files) : [event.target.files[0]];

        for (const file of files) {
            try {
                const uploadedUrl = await uploadImage(file);
                if (isMultiple) {
                    setImageURl((prevState) => ({
                        ...prevState,
                        images: [...prevState.images, uploadedUrl],
                    }));
                } else {
                    setImageURl((prevState) => ({
                        ...prevState,
                        icon: uploadedUrl,
                    }));
                }
            } catch (error: any) {
                showToastNotification('danger', error.message);
            }
        }
    };

    const handleDeleteImage = async (e: React.MouseEvent<HTMLButtonElement>, url: string, isMultiple: boolean) => {
        e.preventDefault()
        if (isMultiple) {
            setImageURl((prevState) => ({
                ...prevState,
                images: prevState.images.filter((image) => image !== url),
            }));
        } else {
            setImageURl((prevState) => ({
                ...prevState,
                icon: '',
            }));
        }
        await deleteFile(url);
    };
    return (
        <Layout>
            <h1 className="app-page-title">Add Section</h1>
            <div className="app-card app-card-settings shadow-sm p-4">
                <div className="app-card-body">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">
                            {/* Page ID */}
                            <div className="mb-3 col-12 col-md-6">
                                <SelectDropdown
                                    options={pageLinks}
                                    register={register('page', { required: 'Page is required' })}
                                    name="page"
                                    label="Page"
                                />
                                <ErrorMessage message={errors.page?.message?.toString()} />
                            </div>

                            <div className="mb-3 col-12 col-md-6">
                                <InputField
                                    type="text"
                                    register={register('title', { required: 'Heading is required' })}
                                    name="title"
                                    label="Heading"
                                />
                                <ErrorMessage message={errors.heading?.message?.toString()} />
                            </div>

                            {/* Content */}
                            <div className="mb-3 col-12 col-md-12">
                                <label htmlFor="content" className="form-label">Content</label>
                                <Controller
                                    name="content"
                                    control={control}
                                    rules={{ required: false }}
                                    render={({ field }) => (
                                        <CKEditorField
                                            initialValue={field.value || ''}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                                <ErrorMessage message={errors.content?.message?.toString()} />
                            </div>
                            <div className="mb-3 col-12 col-md-12">
                                <div>
                                    <InputField
                                        type="text"
                                        register={register('order', { required: 'Order is required' })}
                                        name="order"
                                        label="Order"
                                    />
                                    <ErrorMessage message={errors.order?.message?.toString()} />
                                </div>
                                <div className="d-flex align-items-center gap-3 my-3">
                                    {Object.keys(sections).map((key) => (
                                        <label key={key} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                            <input
                                                className=" form-check-input"
                                                type="checkbox"
                                                checked={sections[key as keyof typeof sections]}
                                                onChange={() => toggleSection(key as keyof typeof sections)}
                                            />
                                            Add {key.charAt(0).toUpperCase() + key.slice(1)}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {sections.image &&
                                <div className="mb-3 col-12">
                                    <div className=" form-label">
                                        Upload Image
                                    </div>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <label htmlFor="file-input-2" className="choosefilenew position-relative">
                                                <input type="file" className="form-control" id="file-input-2" multiple accept="image/*" hidden onChange={(e) => handleFileChange(e, true)} />
                                                <div><div className='mb-2'><i className="bi fs-4 bi-upload"></i></div>Upload Image</div>
                                            </label>
                                        </div>
                                        <div className="col-md-9">

                                            <div className="addimagefiles row" >
                                                {imageURL.images.map((url, index) => (
                                                    <div className="col-md-3 mb-2">
                                                        <div className="image-card position-relative" key={index}>
                                                            <img src={url} alt={`Uploaded ${index}`} className="" />
                                                            <button className="btn btn-danger px-2 py-1 text-white position-absolute" type="button" onClick={(e) => handleDeleteImage(e, url, true)}>														<i className='bi bi-trash3'></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                        </div>
                                    </div>

                                </div>
                            }
                            {/* Icon */}
                            {sections.icon && <div className="mb-3 col-12 ">
                                <div className="form-label">
                                    Upload Icon
                                </div>
                                <div className="row">
                                    <div className="col-md-3">
                                        <label htmlFor="file-input" className="choosefilenew position-relative">

                                            <input type="file" id="file-input" className="form-control" accept="image/*" hidden onChange={(e) => handleFileChange(e, false)} />

                                            <div><div className='mb-2'><i className="bi fs-4 bi-upload"></i></div>Upload Icon</div>

                                        </label>

                                    </div>
                                    <div className="col-md-9">

                                        <div className="addimagefiles row">
                                            {imageURL.icon && (
                                                <div className="col-md-3 mb-2">
                                                    <div className="image-card">
                                                        <img src={imageURL.icon} alt="Icon" className="image-preview" />
                                                        <button className="btn btn-danger px-2 py-1 text-white position-absolute" type="button" onClick={(e) => handleDeleteImage(e, imageURL.icon, false)}>														<i className='bi bi-trash3'></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                            </div>}

                            {sections.subSection && <>
                                <h5>Subsection</h5>
                                <div className="mb-3 col-12  ">
                                    <InputField
                                        type="text"
                                        register={register('sub_heading', { required: 'Content is required' })}
                                        name="sub_heading"
                                        label="Heading"
                                    />
                                    <ErrorMessage message={errors.sub_heading?.message?.toString()} />
                                </div>
                                <div className="mb-3 col-12 ">
                                    <label htmlFor="content" className="form-label">Content</label>
                                    <Controller
                                        name="sub_content"
                                        control={control}
                                        rules={{ required: 'Content is required' }}
                                        render={({ field }) => (
                                            <CKEditorField
                                                initialValue={field.value || ''}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                    <ErrorMessage message={errors.sub_content?.message?.toString()} />
                                </div>
                            </>}
                            {/* Button */}
                            {sections.button && <>
                                <h5>Button</h5>
                                <div className="mb-3 col-12 col-md-6">
                                    <InputField
                                        type="text"
                                        register={register('button_title', { required: 'Title is required' })}
                                        name="button_title"
                                        label="Title"
                                    />
                                    <ErrorMessage message={errors.button_title?.message?.toString()} />
                                </div>
                                <div className="mb-3 col-12 col-md-6">
                                    <SelectDropdown
                                        options={pageLinks}
                                        register={register('button_url', { required: 'URL is required' })}
                                        name="button_url"
                                        label="URL"
                                    />
                                    <ErrorMessage message={errors.button_url?.message?.toString()} />
                                </div>
                            </>}
                            {/* Link */}
                            {sections.link &&
                                <>
                                    <h5>Link</h5>
                                    <div className="mb-3 col-12 col-md-6">
                                        <InputField
                                            type="text"
                                            register={register('link_title', { required: 'Title is required' })}
                                            name="link_title"
                                            label="Title"
                                        />
                                        <ErrorMessage message={errors.link_title?.message?.toString()} />
                                    </div>
                                    <div className="mb-3 col-12 col-md-6">
                                        <SelectDropdown
                                            options={pageLinks}
                                            register={register('link_url', { required: 'URL is required' })}
                                            name="link_url"
                                            label="URL"
                                        />
                                        <ErrorMessage message={errors.link_url?.message?.toString()} />
                                    </div>
                                </>}
                        </div>
                        <div className="col-12 text-end mt-3 ">
                            <Button type="submit" loading={loading} className="btn btn-primary text-white px-5" text="Submit" />
                        </div>
                    </form>
                </div>
                {/*//app-card-body*/}
            </div>
        </Layout>
    )
}

export default AddSection